import { IGroup, MessageBarType } from '@fluentui/react';
import { DetailsList, IconButton, MessageBar, TooltipHost, useTheme } from '@h2oai/ui-kit';
import { useEffect, useMemo, useState } from 'react';

import { LoginEventsRequest, LoginEventsResponse } from '../../../../../admin-center/gen/user_login/userlogin_pb';
import { useAdminCenterService } from '../../../../../admin-center/hooks';
import { useUser } from '../../../../../utils/hooks';
import { formatDefaultDateAndTimeWithSeconds } from '../../../../../utils/utils';
import {
  columnHeaderContainerStyles,
  columnStyles,
  customTableStyles,
  groupIconStyle,
  tooltipHostStyles,
} from '../../../common/common.styles';
import { buildUserContext, getDummyList } from '../../../common/utils';
import { UserLoginColumnKey, UserLoginColumnName } from '../../../constants';
import SkeletonLoader from '../../SkeletonLoader/SkeletonLoader';
import { TableStyle } from '../UserLogin.styles';

interface UserLogin {
  userLoginName: string;
  userLoginDate: string;
  userLoginTime: string;
}

interface UserLoginTableProps {
  startDate: string;
  endDate: string;
  searchQuery: string;
}

const dummyList = getDummyList<UserLogin>(8);

export default function UserLoginTable({ startDate, endDate, searchQuery }: UserLoginTableProps) {
  const service = useAdminCenterService();
  const theme = useTheme();
  const user = useUser();
  const [userLoginData, setUserLoginData] = useState<UserLogin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [groupByKey, setGroupByKey] = useState<string | undefined>(undefined);

  const fetchLoginEvents = async () => {
    setIsLoading(true);
    try {
      const userContext = await buildUserContext(user);
      const request: LoginEventsRequest = {
        startDate: startDate,
        endDate: endDate,
        userContext: userContext,
      };

      const response: LoginEventsResponse = await service.getUserLoginEvents(request);

      const formattedData = (response.events ?? []).map((event) => {
        const utcDate = event.ts ? new Date(event.ts) : null;

        return {
          userLoginName: event.userName ?? '-',
          userLoginDate: formatDefaultDateAndTimeWithSeconds(utcDate) || '-',
          userLoginTime: utcDate
            ? utcDate.toLocaleTimeString('en-US', {
                hour12: false,
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              })
            : '-',
        };
      });

      setUserLoginData(formattedData);
    } catch (error) {
      console.error('Error fetching login events:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLoginEvents();
  }, [startDate, endDate]);

  const filteredData = useMemo(() => {
    const searchTerm = searchQuery.toLowerCase();
    return userLoginData.filter(
      (item) =>
        item.userLoginDate.toLowerCase().includes(searchTerm) ||
        item.userLoginName.toLowerCase().includes(searchTerm) ||
        item.userLoginTime.toLowerCase().includes(searchTerm)
    );
  }, [userLoginData, searchQuery]);

  const { groupedItems, groups } = useMemo(() => {
    if (!groupByKey) return { groupedItems: filteredData, groups: undefined };

    const groupedMap = filteredData.reduce((acc, item) => {
      const key = item[groupByKey as keyof UserLogin] as string;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {} as Record<string, UserLogin[]>);

    const groupedItems: UserLogin[] = [];
    const groups: IGroup[] = [];

    let startIndex = 0;
    Object.entries(groupedMap).forEach(([groupName, items], _index) => {
      groupedItems.push(...items);
      groups.push({
        key: groupName,
        name: groupName,
        startIndex,
        count: items.length,
        level: 0,
      });
      startIndex += items.length;
    });

    return { groupedItems, groups };
  }, [filteredData, groupByKey]);

  const renderHeader = (colName: string, colKey: string) => (
    <div style={columnHeaderContainerStyles}>
      {colName}
      <TooltipHost styles={tooltipHostStyles} content={`Group by ${colName}`}>
        <IconButton
          iconProps={{ iconName: 'GroupList' }}
          styles={groupIconStyle(groupByKey === colKey, theme)}
          onClick={(e) => {
            e.stopPropagation();
            setGroupByKey(groupByKey !== colKey ? colKey : undefined);
          }}
        />
      </TooltipHost>
    </div>
  );

  const columns = [
    {
      key: UserLoginColumnKey.name,
      fieldName: UserLoginColumnKey.name,
      name: UserLoginColumnName.name,
      minWidth: 200,
      onRender: (item: UserLogin) => (
        <>{!isLoading ? item.userLoginName : <SkeletonLoader width="65%" height="100%" />}</>
      ),
      onRenderHeader: () => renderHeader(UserLoginColumnName.name, UserLoginColumnKey.name),
      styles: columnStyles(false),
    },
    {
      key: UserLoginColumnKey.date,
      fieldName: UserLoginColumnKey.date,
      name: UserLoginColumnName.date,
      minWidth: 200,
      onRender: (item: UserLogin) => (
        <>{!isLoading ? item.userLoginDate : <SkeletonLoader width="65%" height="100%" />}</>
      ),
    },
    {
      key: UserLoginColumnKey.time,
      fieldName: UserLoginColumnKey.time,
      name: UserLoginColumnName.time,
      minWidth: 200,
      onRender: (item: UserLogin) => (
        <>{!isLoading ? item.userLoginTime : <SkeletonLoader width="65%" height="100%" />}</>
      ),
    },
  ];

  return (
    <div style={TableStyle(theme)}>
      {!isLoading && userLoginData.length === 0 ? (
        <MessageBar messageBarType={MessageBarType.warning}>
          {'No data available for the selected date range'}
        </MessageBar>
      ) : (
        <DetailsList
          columns={columns}
          styles={customTableStyles(theme, '593')}
          items={isLoading ? dummyList : groupedItems}
          groups={isLoading ? undefined : groups}
        />
      )}
    </div>
  );
}
